/* eslint-disable no-empty */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { IMaskInput } from "react-imask";

/* ================== constants ================== */
const ANNUAL_INCOMES = [
  { id: 1, name: "$12,000-$49,999" },
  { id: 2, name: "$50,000-$99,999" },
  { id: 3, name: "$100,000-$249,999" },
  { id: 4, name: "$250,000-$499,999" },
  { id: 5, name: "$500,000+" },
];

const FIELD_CONFIG = [
  { type: "text", name: "firstname", label: "First Name *" },
  { type: "text", name: "lastname",  label: "Last Name *" },
  { type: "email",name: "email",     label: "Email Address *" },
  { type: "tel",  name: "phone",     label: "Phone Number *", mask: "(000) 000-0000" },
  { type: "text", name: "zip",       label: "Zip Code *",     mask: "00000000" },
];

const PHONE_REGEX = /^\(\d{3}\) \d{3}-\d{4}$/;
const ZIP_REGEX   = /^(\d{5}|\d{8})$/;

/* utils */
// Hosts that actually serve /.netlify/functions/api-proxy. Optimizers like
// NitroPack rehost the widget JS on the WordPress origin while keeping the
// original tools host inside the rewritten path, so match a known host
// anywhere in the src rather than trusting the URL's origin.
const TOOLS_HOSTS = ["tools.unclekam.com", "tools-unclekam.netlify.app"];

function toolsOriginFromSrc(src) {
  if (!src) return "";
  try {
    const u = new URL(src, location.href);
    if (TOOLS_HOSTS.includes(u.hostname)) return u.origin;
  } catch {}
  for (const host of TOOLS_HOSTS) {
    if (src.includes(`/${host}/`) || src.includes(`//${host}/`)) {
      return `https://${host}`;
    }
  }
  return "";
}

function detectToolsOrigin() {
  const fromCur = toolsOriginFromSrc(document.currentScript && document.currentScript.src);
  if (fromCur) return fromCur;
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const fromScan = toolsOriginFromSrc(scripts[i].src || "");
    if (fromScan) return fromScan;
  }
  const isQA = new URLSearchParams(location.search).has("qa_mode");
  return isQA ? "https://tools-unclekam.netlify.app" : "https://tools.unclekam.com";
}

// Step header
const StepHeader = React.memo(function StepHeader({ isDark, currentStep, totalSteps }) {
  const progressPct = Math.round((currentStep / totalSteps) * 100);
  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2 pt-2 pb-2">
        <span className={isDark ? "text-light" : "text-dark"}>{`Step ${currentStep} of ${totalSteps}`}</span>
        <span className={isDark ? "text-light" : "text-dark"}>{`${progressPct}% Complete`}</span>
      </div>
      <div className="ukf-progress-track mb-3">
        <div className={`ukf-progress-bar ${isDark ? "dark" : ""}`} style={{ width: `${progressPct}%` }} />
      </div>
    </>
  );
});

// Step 1
const Step1 = React.memo(function Step1({ formData, setField }) {
  return (
    <div className="row g-3 mt-4">
      {FIELD_CONFIG.map((cfg) => {
        const colClass = cfg.name === "firstname" || cfg.name === "lastname" ? "col-md-6" : "col-12";
        const val = formData[cfg.name] || "";

        return (
          <div className={colClass} key={cfg.name}>
            {cfg.mask ? (
              <IMaskInput
                id={cfg.name}
                name={cfg.name}
                className="form-control"
                placeholder={cfg.label}
                mask={cfg.mask}
                lazy={true}
                overwrite={true}
                defaultValue={val}
                onAccept={(v) => { if (v !== formData[cfg.name]) setField(cfg.name, v); }}
                type={cfg.type}
                inputMode={cfg.name === "zip" ? "numeric" : "tel"}
                autoComplete="off"
              />
            ) : (
              <input
                id={cfg.name}
                name={cfg.name}
                className="form-control"
                placeholder={cfg.label}
                type={cfg.type}
                value={val}
                onChange={(e) => setField(cfg.name, e.target.value)}
                autoComplete="off"
              />
            )}
          </div>
        );
      })}
    </div>
  );
});

const Step2 = React.memo(function Step2({ isDark, services, servicesLoading, formData, setFormData }) {
  return (
    <div>
      <h4 className={`mb-3 ${isDark ? "text-light" : "text-dark"} text-center`}>What can we help you with?</h4>

      {servicesLoading && <div className="text-muted mb-2">Loading services…</div>}
      {!servicesLoading && services.length === 0 && <div className="text-muted mb-2">No services found.</div>}

      <div className="d-flex flex-column gap-2">
        {services.map((s) => {
          const checked = formData.options.includes(String(s.id));
          return (
            <label key={s.id} className={`${isDark ? "text-light" : "text-dark"} ukf-pointer`}>
              <input
                type="checkbox"
                className="form-check-input me-2"
                checked={checked}
                onChange={(e) => {
                  const id = String(s.id);
                  setFormData((prev) => {
                    const next = new Set(prev.options.map(String));
                    if (e.target.checked) next.add(id); else next.delete(id);
                    return { ...prev, options: Array.from(next) };
                  });
                }}
              />
              {s.name}
            </label>
          );
        })}
      </div>
    </div>
  );
});

const Step3 = React.memo(function Step3({ isDark, formData, setField, submitError }) {
  return (
    <div className="mt-4">
      <h4 className={`mb-3 text-center ${isDark ? "text-light" : "text-dark"}`}>What is your annual income?</h4>
      <div className="d-flex flex-column gap-2">
        {ANNUAL_INCOMES.map((inc) => (
          <label key={inc.id} className={`${isDark ? "text-light" : "text-dark"} ukf-pointer`}>
            <input
              type="radio"
              name="annual_income"
              className="form-check-input me-2"
              checked={String(formData.income) === String(inc.id)}
              value={inc.id}
              onChange={(e) => setField("income", e.target.value)}
            />
            {inc.name}
          </label>
        ))}
      </div>

      {!!submitError && <div className="alert alert-danger mt-3" role="alert">{submitError}</div>}

      <div className="mt-3">
        <label htmlFor="ukf_user_agreement" className={`${isDark ? "text-light" : "text-dark"} ukf-pointer`}>
          <input
            id="ukf_user_agreement"
            type="checkbox"
            className="form-check-input me-2"
            checked={!!formData.agreementAccepted}
            onChange={(e) => setField("agreementAccepted", e.target.checked)}
          />
          <span>
            I agree to the{" "}
            <a className={`ukf-muted-link ${isDark ? "text-light" : ""}`} href="https://app.unclekam.com/user-agreement" target="_blank" rel="noreferrer">Uncle Kam Terms of Service</a>{" "}
            and{" "}
            <a className={`ukf-muted-link ${isDark ? "text-light" : ""}`} href="https://app.unclekam.com/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>{" "}
            and agree to receive emails and texts about promotions at the phone number and email provided, and understand this consent is not required to purchase. By checking this box you agree to receive text messages from; Reply STOP to opt out; Reply HELP for help; Message frequency varies; Message and data rates may apply.
          </span>
        </label>
      </div>
    </div>
  );
});

const Step4 = React.memo(function Step4({ isDark, services, formData, redirectUrl }) {
  const selectedServiceNames = services
    .filter((s) => formData.options.map(String).includes(String(s.id)))
    .map((s) => s.name);
  const incomeText = ANNUAL_INCOMES.find((i) => String(i.id) === String(formData.income))?.name || formData.income;

  const summary = [
    ["firstname", formData.firstname],
    ["lastname",  formData.lastname],
    ["email",     formData.email],
    ["phone",     formData.phone],
    ["zip",       formData.zip],
    ["services",  selectedServiceNames.join(", ") || "—"],
    ["income",    incomeText || "—"],
    ["agreementAccepted", String(!!formData.agreementAccepted)],
  ];

  return (
    <div className={`${isDark ? "text-light" : "text-dark"}`}>
      <div className="text-center my-3">
        <div className="ukf-success-check text-success mb-2">✓</div>
        <h4 className="fw-bold">Form submitted successfully</h4>
        <p className="mb-0">Thanks for reaching out to a tax advisor.</p>
      </div>
      {redirectUrl && <p className="text-center mt-3" aria-live="polite">Redirecting...</p>}
      <ul className="mt-3">
        {summary.map(([k, v]) => (<li key={k}><span className="fw-semibold">{k}:</span> {v}</li>))}
      </ul>
    </div>
  );
});

export default function MultiStepLeadForm({
  theme = "light",
  qa_mode,
  data_source = "VSL",
  redirectUrl,
  fromBooking = false,
  pro_id,
  proxyUrl,                 // NEW: allow widget to pass in proxyUrl
}) {
  const css = `
    .ukf-progress-track{height:6px;background:#e9ecef;border-radius:999px}
    .ukf-progress-bar{height:6px;border-radius:999px;background:#4f58ff;transition:width .25s}
    .ukf-progress-bar.dark{background:#5a6bff}
    .ukf-success-check{font-size:2.75rem;line-height:1}
    .ukf-pointer{cursor:pointer}
    .ukf-muted-link{text-decoration:underline}
  `;

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const qa = useMemo(() => (typeof qa_mode === "boolean" ? qa_mode : params.has("qa_mode")), [qa_mode, params]);
  const isDark = theme === "dark";

  // Robust proxy URL
  const PROXY_URL =
    proxyUrl ||
    window.__UK_PROXY_URL__ ||
    `${detectToolsOrigin()}/.netlify/functions/api-proxy`;

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const proId = params.get("pro_id");

  const [formData, setFormData] = useState(() => ({
    firstname: params.get("firstname") || "",
    lastname:  params.get("lastname")  || "",
    email:     params.get("email")     || "",
    phone:     params.get("phone")     || "",
    zip:       params.get("zip")       || "",
    options:   [],
    income:    "",
    agreementAccepted: false,
  }));

  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // fetch services when entering step 2
  useEffect(() => {
    if (currentStep !== 2) return;
    let active = true;
    (async () => {
      setServicesLoading(true);
      try {
        const { data } = await axios.post(
          PROXY_URL,
          { path: "/get-services", qa_mode: qa, method: "GET" },
          { headers: { "Content-Type": "application/json" } }
        );
        if (active) setServices(Array.isArray(data) ? data : []);
      } catch {
        if (active) setServices([]);
      } finally {
        if (active) setServicesLoading(false);
      }
    })();
    return () => { active = false; };
  }, [currentStep, PROXY_URL, qa]);

  const setField = (name, value) =>
    setFormData((s) => (s[name] === value ? s : { ...s, [name]: value }));

  const phoneComplete = PHONE_REGEX.test(formData.phone);
  const zipComplete   = ZIP_REGEX.test(formData.zip);
  const step1AllFilled = ["firstname","lastname","email","phone","zip"].every((n) => String(formData[n] || "").trim() !== "");
  const step1Valid = step1AllFilled && phoneComplete && zipComplete;
  const step2Valid = (formData.options || []).length > 0;
  const step3Valid = !!formData.income && !!formData.agreementAccepted;

  const goPrev = () => setCurrentStep((s) => Math.max(1, s - 1));
  const goNext = () => {
    if (currentStep === 1 && !step1Valid) return;
    if (currentStep === 2 && !step2Valid) return;
    if (currentStep === 3 && !step3Valid) return;
    setSubmitError("");
    setCurrentStep((s) => Math.min(totalSteps, s + 1));
  };

  const handleSubmit = async () => {
    if (!step3Valid || submitting) return;
    setSubmitting(true);
    setSubmitError("");

    const lead = {
      ...formData,
      phone: (formData.phone || "").replace(/[ ()-]/g, ""),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    try {
      try {
        const ipRes = await axios.get("https://api.ipify.org?format=json");
        lead.clientIp = ipRes?.data?.ip || null;
      } catch { lead.clientIp = null; }

      await axios.post(
        PROXY_URL,
        {
          path: "/generate-lead",
          qa_mode: qa,
          data: lead,
          params: { tag: data_source || "VSL", pro_id: proId || pro_id }
        },
        { headers: { "Content-Type": "application/json" } }
      );
if (redirectUrl) {
  const delayMs = 4000;
  const email = formData?.email;

  setTimeout(() => {
    // Build target URL (preserves any query already on redirectUrl)
    const url = new URL(redirectUrl, window.location.href);

    // Merge in ALL existing query params from the current page
    // (don’t overwrite anything already present on redirectUrl)
    const srcParams = new URLSearchParams(window.location.search);
    srcParams.forEach((value, key) => {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });

    // Finally, inject/override the email param
    if (email) url.searchParams.set("email", email);

    window.location.assign(url.toString());
  }, delayMs);
}

      setCurrentStep(4);
    } catch (err) {
      const msg = err?.response?.data?.message || "Server error. Please try again later.";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const NextButton = () => {
    const isSubmitStep = currentStep === 3;
    const isLastStep = currentStep === 4;
    const disabled =
      (currentStep === 1 && !step1Valid) ||
      (currentStep === 2 && !step2Valid) ||
      (isSubmitStep && (!step3Valid || submitting)) ||
      isLastStep;

    return (
      <button
        type="button"
        disabled={disabled}
        onClick={isSubmitStep ? handleSubmit : goNext}
        className={`btn ${isSubmitStep ? "btn-success" : "btn-primary"} px-4`}
      >
        {submitting ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            Submitting…
          </>
        ) : isSubmitStep ? "Submit" : "Next ⇒"}
      </button>
    );
  };

  return (
    <div className={`container px-3 px-md-4 py-3 ${isDark ? "" : ""}`}>
      <style>{css}</style>
      <div className="mx-auto" style={{ maxWidth: 860 }}>
        {!fromBooking && (
          <div className="mb-2 px-3">
            <StepHeader isDark={isDark} currentStep={currentStep} totalSteps={totalSteps} />
          </div>
        )}

        <form onSubmit={(e) => e.preventDefault()}>
          <div className="mb-3 mt-4">
            {currentStep === 1 && <Step1 isDark={isDark} formData={formData} setField={setField} />}
            {currentStep === 2 && (
              <Step2
                isDark={isDark}
                services={services}
                servicesLoading={servicesLoading}
                formData={formData}
                setFormData={setFormData}
              />
            )}
            {currentStep === 3 && (
              <Step3
                isDark={isDark}
                formData={formData}
                setField={setField}
                submitError={submitError}
              />
            )}
            {currentStep === 4 && (
              <Step4 isDark={isDark} services={services} formData={formData} redirectUrl={redirectUrl} />
            )}
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            {currentStep > 1 && currentStep !== 4 ? (
              <button type="button" onClick={goPrev} className="btn btn-link text-decoration-none px-0">
                ⇐ Previous
              </button>
            ) : <span />}
            {currentStep < 4 && <NextButton />}
          </div>
        </form>
      </div>
    </div>
  );
}
